/**
 * MatchingPage — DTF Film Matching station (design-sprint Concept B).
 *
 * Flow (put-to-light two-step, mirrors CatcherPage/PressingPage): scan a
 * garment QR → the screen shows WHICH pigeon hole holds its film (big number)
 * → operator pulls the film, lays it on the garment → scans the film to
 * confirm the match. The confirm sets the film STORED → MATCHED and releases
 * the slot (the film travels on the garment from here). Matched garments
 * stack up for the press, which needs no retrieval or film-scan step.
 *
 * A film↔garment MISMATCH raises a persistent, explicit-dismiss blocking
 * modal (image-first, red shake + buzz), never a toast (PRODUCTION_DESIGN
 * §6.1). Report issue routes to the canonical film-only redo
 * (catcher.createRedo(film_only)).
 *
 * Follows PRODUCTION_DESIGN.md: scanner-first, ≥48px targets, modal-level
 * errors with audio, semantic colour for state, one blocking modal at a time,
 * scanner auto-focus paused ONLY while a modal is open.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from '@/contexts/LocationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { QRScannerInput } from '@/features/catcher-station';
import { FilmLocationDirective } from '@/features/film-storage';
import { useScanFeedback } from '@/hooks/useScanFeedback';
import { useScannerAutoFocus } from '@/hooks/useScannerAutoFocus';
import { ScanErrorModal } from '@/components/ui/scan-error-modal';
import { FilmMismatchModal } from '@/features/pressing/components/FilmMismatchModal';
import { FilmRedoDialog } from '@/components/production/FilmRedoDialog';
import { trpc } from '@/lib/trpc';
import { extractGarmentId, extractFilmId } from '@/lib/qr-utils';
import { getUserMessage, parseOrderOnHold } from '@/lib/trpc-error-utils';
import { playSuccessBeep } from '@/lib/sounds';
import { useBackendHeartbeat } from '@/hooks/useBackendHeartbeat';
import { ConnectionStatusBanner } from '@/components/ui/connection-status-banner';
import { toast } from 'sonner';
import {
  ArrowLeft, CheckCircle, Loader2, ScanLine, Shirt, AlertCircle, Layers,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ZoomImg } from '@/components/production/HoverZoom';

type GarmentForMatch = Awaited<ReturnType<ReturnType<typeof trpc.useUtils>['matching']['getGarmentForMatch']['fetch']>>;

export function MatchingPage() {
  const { t } = useTranslation('production');
  const { selectedLocation } = useLocation();
  const heartbeat = useBackendHeartbeat();
  const feedback = useScanFeedback();
  const utils = trpc.useUtils();

  const confirmMutation = trpc.matching.confirmMatch.useMutation();
  const redoMutation = trpc.catcher.createRedo.useMutation();

  const [garment, setGarment] = useState<GarmentForMatch | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [matched, setMatched] = useState(false);
  const [showMismatch, setShowMismatch] = useState(false);
  const [mismatchSeq, setMismatchSeq] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);

  const shownErrorRef = useRef<string | null>(null);
  const confirmingRef = useRef(false);

  // Clear on location change.
  useEffect(() => {
    setGarment(null);
    setMatched(false);
    setShowMismatch(false);
    shownErrorRef.current = null;
  }, [selectedLocation?.id]);

  const clearGarment = useCallback(() => {
    setGarment(null);
    setMatched(false);
    setShowMismatch(false);
  }, []);

  const anyModalOpen = showMismatch || feedback.isModalOpen || reportOpen;
  useScannerAutoFocus({ paused: anyModalOpen });

  const lookupGarment = useCallback(async (garmentUnitId: string) => {
    if (!selectedLocation?.id) return;
    setIsLookingUp(true);
    try {
      const result = await utils.matching.getGarmentForMatch.fetch({
        garmentUnitId,
        locationId: selectedLocation.id,
      });
      setGarment(result);
      setMatched(result.film?.status === 'MATCHED');
      shownErrorRef.current = null;
    } catch (error) {
      setGarment(null);
      const onHold = parseOrderOnHold(error);
      if (onHold.isOnHold) {
        if (shownErrorRef.current !== 'ON_HOLD') {
          shownErrorRef.current = 'ON_HOLD';
          feedback.triggerOrderOnHold(onHold.holdReason);
        }
      } else {
        const msg = getUserMessage(error);
        if (shownErrorRef.current !== msg) {
          shownErrorRef.current = msg;
          feedback.triggerError(t('matching.lookupError'), msg);
        }
      }
    } finally {
      setIsLookingUp(false);
    }
    // feedback intentionally excluded — recreated every render (unstable-object gotcha)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, utils.matching.getGarmentForMatch, t]);

  const confirmMatch = useCallback(async () => {
    if (!garment?.film || !selectedLocation?.id) return;
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    try {
      const res = await confirmMutation.mutateAsync({
        garmentUnitId: garment.garmentUnitId,
        filmUnitId: garment.film.id,
        locationId: selectedLocation.id,
      });
      if (res.success) {
        // No beep here — QRScannerInput already chimed on capture (double-chime
        // gotcha, mirrors FilmStoragePage). The Matched state is the visual twin.
        setMatched(true);
        void utils.filmStorage.getFilmStorage.invalidate();
        toast.success(t('matching.matched'), { description: garment.productName, duration: 3000 });
      }
    } catch (error) {
      feedback.triggerError(t('matching.matchError'), getUserMessage(error));
    } finally {
      confirmingRef.current = false;
    }
    // feedback excluded (unstable).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [garment, selectedLocation?.id, confirmMutation, utils, t]);

  // Context-aware scan (mirrors PressingPage):
  //  - no garment → scan a GARMENT QR (look it up);
  //  - garment on screen, awaiting the film scan → the film's composited QR
  //    decodes to the SAME garment id (tile-margin) or a FILM: id bound to
  //    this garment → confirm; anything else valid → MISMATCH modal;
  //  - already matched → a different garment QR starts the next match.
  const handleScan = useCallback((qrCode: string) => {
    const scannedId = extractGarmentId(qrCode);
    const scannedFilmId = extractFilmId(qrCode);

    if (!garment) {
      if (!scannedId) {
        feedback.triggerError(t('matching.scanError'), t('matching.scanErrorDesc'));
        return;
      }
      void lookupGarment(scannedId);
      return;
    }

    const awaitingConfirm = !!garment.film && !matched;

    if (scannedFilmId) {
      if (awaitingConfirm && garment.film && scannedFilmId === garment.film.id) {
        void confirmMatch();
        return;
      }
      if (awaitingConfirm) {
        setMismatchSeq((n) => n + 1);
        setShowMismatch(true);
        return;
      }
      feedback.triggerError(t('scanFeedback.wrongQr'), t('matching.scanErrorDesc'));
      return;
    }

    if (!scannedId) {
      feedback.triggerError(t('scanFeedback.wrongQr'), t('matching.scanErrorDesc'));
      return;
    }

    if (scannedId === garment.garmentUnitId) {
      if (awaitingConfirm) {
        // Film label carries the garment's composited QR — same id = match.
        void confirmMatch();
        return;
      }
      toast.info(t('matching.alreadyMatched'), { duration: 1500 });
      return;
    }

    // A DIFFERENT valid garment id.
    if (awaitingConfirm) {
      setMismatchSeq((n) => n + 1);
      setShowMismatch(true);
      return;
    }
    clearGarment();
    void lookupGarment(scannedId);
    // feedback excluded (unstable).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [garment, matched, lookupGarment, clearGarment, confirmMatch, t]);

  const handleReportConfirm = useCallback(async (reason: string) => {
    if (!garment || !selectedLocation?.id) return;
    try {
      const result = await redoMutation.mutateAsync({
        garmentUnitId: garment.garmentUnitId,
        locationId: selectedLocation.id,
        reason,
        redoSubtype: 'film_only' as const,
      });
      if (result.success) {
        setReportOpen(false);
        playSuccessBeep();
        toast.success(t('matching.reportDone'), { duration: 3000 });
        void utils.filmStorage.getFilmStorage.invalidate();
        clearGarment();
      }
    } catch (error) {
      setReportOpen(false);
      feedback.triggerError(t('matching.reportFailed'), getUserMessage(error));
    }
    // feedback excluded (unstable).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [garment, selectedLocation?.id, redoMutation, utils, clearGarment, t]);

  if (!selectedLocation) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl animate-enter">
        <Card>
          <CardContent className="py-16 text-center">
            <Layers className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">{t('matching.selectLocation')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasGarment = !!garment;

  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter flex flex-col min-h-[calc(100dvh-4.5rem)]">
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {isLookingUp && t('lookingUp')}
        {garment && t('matching.liveStatus', { product: garment.productName })}
      </div>

      {/* Header */}
      <div className="flex items-center gap-3">
        {hasGarment && (
          <Button variant="ghost" onClick={clearGarment} className="shrink-0 gap-1.5" aria-label={t('backTo', { station: t('matching.title') })}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <div className="p-2 rounded-lg bg-fuchsia-500/10">
          <Layers className="h-6 w-6 text-fuchsia-600 dark:text-fuchsia-400" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">{t('matching.title')}</h1>
          <p className="text-muted-foreground">{t('matching.description')}</p>
        </div>
      </div>

      <ConnectionStatusBanner heartbeat={heartbeat} />

      <div className="flex-1 space-y-6">
        {/* ACTIVE: garment + retrieval directive */}
        {hasGarment && garment && (
          <>
            <Card className={cn('transition-all', matched && 'border-l-4 border-l-emerald-500', garment.isRedo && !matched && 'border-l-4 border-l-amber-500')}>
              <CardContent className="pt-5 pb-4 space-y-4">
                {/* Garment identity */}
                <div className="flex items-start gap-4">
                  {(garment.customPreviewUrl || garment.imageUrl) ? (
                    <ZoomImg src={garment.customPreviewUrl ?? garment.imageUrl ?? undefined} alt="" decoding="async" className="h-24 w-24 rounded object-cover shrink-0" />
                  ) : (
                    <div className="h-24 w-24 rounded bg-muted flex items-center justify-center shrink-0">
                      <Shirt className="h-10 w-10 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-lg font-semibold leading-tight">{garment.productName}</p>
                    <p className="font-mono text-sm text-muted-foreground">{garment.sku}</p>
                    <div className="flex flex-wrap items-center gap-2 pt-1.5">
                      {garment.size && <Badge variant="secondary" className="text-sm">{garment.size}</Badge>}
                      {garment.color && <span className="text-sm">{garment.color}</span>}
                      <Badge className="bg-fuchsia-100 text-fuchsia-800 hover:bg-fuchsia-100 dark:bg-fuchsia-900/50 dark:text-fuchsia-200 text-sm">DTF</Badge>
                      {garment.film?.isDual && (
                        <Badge variant="outline" className="text-sm gap-1"><Layers className="h-3.5 w-3.5" aria-hidden />{t('matching.dualSides')}</Badge>
                      )}
                      {garment.isRedo && <Badge variant="destructive" className="text-sm">REDO</Badge>}
                      {garment.orderNumber && <span className="text-sm text-muted-foreground">{garment.orderNumber}</span>}
                    </div>
                  </div>
                  {matched && (
                    <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300 shrink-0">
                      <CheckCircle className="h-6 w-6" />
                      <span className="text-base font-semibold">{t('matching.matched')}</span>
                    </div>
                  )}
                </div>

                {/* Retrieval directive / no-film state */}
                {garment.film ? (
                  garment.storage && !matched ? (
                    <FilmLocationDirective
                      slotLabel={garment.storage.label}
                      storageType={garment.storage.storageType}
                      orderName={garment.storage.currentOrderName ?? garment.orderNumber}
                      orderNumber={garment.storage.currentOrderNumber}
                      designHint={garment.storage.designGroup ?? garment.productName}
                      sizeHint={garment.size}
                    />
                  ) : !matched ? (
                    <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-4 flex items-start gap-3">
                      <AlertCircle className="h-6 w-6 text-amber-600 dark:text-amber-400 shrink-0" />
                      <p className="text-base font-semibold text-amber-800 dark:text-amber-200">{t('matching.filmNotStored')}</p>
                    </div>
                  ) : null
                ) : (
                  <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-4 flex items-start gap-3">
                    <AlertCircle className="h-6 w-6 text-amber-600 dark:text-amber-400 shrink-0" />
                    <div>
                      <p className="text-base font-semibold text-amber-800 dark:text-amber-200">{t('matching.noFilm')}</p>
                      <p className="text-sm text-amber-700 dark:text-amber-300">{t('matching.noFilmDesc')}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            {/* Hidden scanner — scan-to-advance (film scan) in the active view. */}
            <div className="sr-only">
              <QRScannerInput onScan={handleScan} disabled={anyModalOpen} />
            </div>
          </>
        )}

        {/* LOADING */}
        {!hasGarment && isLookingUp && (
          <Card>
            <CardContent className="py-16 text-center">
              <Loader2 className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-spin" />
              <p className="text-lg text-muted-foreground">{t('loadingItem')}</p>
            </CardContent>
          </Card>
        )}

        {/* IDLE: scanner card */}
        {!hasGarment && !isLookingUp && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t('matching.scanGarment')}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <QRScannerInput onScan={handleScan} disabled={anyModalOpen} isLoading={isLookingUp} />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sticky action bar */}
      {hasGarment && garment && (
        <div className="sticky bottom-0 z-10 bg-background pb-4 pt-4 border-t space-y-3">
          {matched ? (
            <Button className="w-full h-16 text-xl font-semibold" variant="outline" onClick={clearGarment}>
              <CheckCircle className="h-6 w-6 mr-2 text-emerald-600" />
              {t('matching.scanNext')}
            </Button>
          ) : !['LABEL_PRINTED', 'PICKED', 'FILM_PRINTED'].includes(garment.garmentStatus) ? (
            <div
              className="w-full h-16 flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 text-xl font-semibold"
              role="status"
              aria-live="polite"
            >
              <AlertCircle className="h-5 w-5" />
              {t('matching.garmentNotReady')}
            </div>
          ) : garment.film && garment.film.status === 'STORED' ? (
            <div
              className="w-full h-16 flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-fuchsia-300 dark:border-fuchsia-700 bg-fuchsia-50 dark:bg-fuchsia-950/30 text-fuchsia-700 dark:text-fuchsia-300 text-xl font-semibold"
              role="status"
              aria-live="polite"
            >
              <ScanLine className="h-5 w-5" />
              {t('matching.scanFilmToConfirm')}
            </div>
          ) : null}

          {/* Report issue — canonical film-only redo. */}
          {!matched && (
            <Button
              variant="outline"
              className="w-full h-12 text-base"
              onClick={() => setReportOpen(true)}
              disabled={redoMutation.isPending}
            >
              {t('matching.reportIssue')}
            </Button>
          )}
        </div>
      )}

      <FilmMismatchModal
        open={showMismatch}
        mismatchSeq={mismatchSeq}
        expectedProductName={garment?.productName ?? ''}
        expectedImageUrl={garment?.customPreviewUrl ?? garment?.imageUrl ?? null}
        onDismiss={() => setShowMismatch(false)}
      />
      <FilmRedoDialog
        open={reportOpen}
        onOpenChange={(o) => { if (!o) setReportOpen(false); }}
        onConfirm={handleReportConfirm}
        isPending={redoMutation.isPending}
      />
      <ScanErrorModal {...feedback.modalProps} />
    </div>
  );
}
